import type { CatalogItem, SpecItem } from '../types';
import { KITCHEN_COMPATIBILITY_RULES_DEFAULT } from '../constants';

export interface UnmatchedAccessory {
  code: string;
  name: string;
}

export interface ParsedXmlProject {
  clientName: string;
  modelGroup: string;
  extraModels?: string[];
  doorMaterial: string;
  extraMaterials?: string[];
  doorColor: string;
  extraDoorColors?: string[];
  bodyMat: string;
  handleCode: string;
  glassType?: string;
  profileColor?: string;
  items: SpecItem[];
  matchedCount: number;
  skippedCount: number;
  unmatchedAccessories: UnmatchedAccessory[];
}

/**
 * Montaj/bağlantı malzemesi gibi teklif ürün listesine alınmaması istenen ürün kodları.
 * XML içe aktarmada bu kodlar katalogla eşleştirilmez, ürün listesine ve
 * "eşleşmeyen aksesuar" listesine eklenmez.
 */
export const XML_IMPORT_EXCLUDED_CODES: ReadonlySet<string> = new Set([
  'MALRTMS20', 'MALRTSB20', 'MARS', 'MBZ12EKP', 'MBZ12EKPSS',
  'MBZ12EKPSYH', 'MBZAYAKYH12', 'MBZAYAKYSET12', 'MBZKP12', 'MBZKP12SS',
  'MBZKP12SYH', 'MBZTABANY', 'MCAMRAFPIM', 'MCTCTPLS', 'MDBDIRSEGI20CM',
  'MKAPBAGPIM', 'MKAPBAGSACMA', 'MKAPBAGSACMS', 'MKAPBAGSACPAL', 'MKAPBAGSACSB',
  'MMAYKCRCA71', 'MMDAR', 'MMDARARA', 'MMENTSE', 'MMENTSE165',
  'MMENTSEKLN', 'MMENTSESGT', 'MMENTSTABAN', 'MMGEN', 'MMGENARA',
  'MMKLN', 'MMNFZAL', 'MMNFZSYH', 'MMU5560', 'MPAYAK',
  'MPMD', 'MPMM', 'MPMM2', 'MPMM4', 'MPMMK',
  'MPRFKLP596', 'MRAFPIM', 'MSABDIRSEGI5CM', 'MSEHIM87', 'MTZBL',
  'MTZKKY', 'MTZSGPKDS', 'MTZSGPKIC', 'MTZSGPKLR',
]);

const normalizeCodeKey = (code: string): string => (code || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
const EXCLUDED_CODE_KEYS = new Set(Array.from(XML_IMPORT_EXCLUDED_CODES, normalizeCodeKey));

export function isExcludedXmlCode(code: string): boolean {
  const key = normalizeCodeKey(code);
  return key.length > 0 && EXCLUDED_CODE_KEYS.has(key);
}

const MATERIAL_ALIASES: Record<string, string> = {
  'kristal mat': 'Mat Kristal Pet',
  'mat kristal': 'Mat Kristal Pet',
  'kristal parlak': 'Parlak Kristal Pet',
  'parlak kristal': 'Parlak Kristal Pet',
  'mat lake': 'Mat Lake',
  'parlak lake': 'Parlak Lake',
  'dokulu melamin': 'Dokulu Melamin',
  'duz melamin': 'Düz Melamin',
  'soft pet': 'Soft Pet',
  'mat pet': 'Mat Pet',
  'akrilik pet': 'Akrilik Pet',
  'soft lak': 'Soft Lak',
  'mat polimerik': 'Mat Polimerik',
  'dogal ahsap kaplama': 'Doğal Ahşap Kaplama',
  'ahsap kaplama': 'Doğal Ahşap Kaplama',
  'metal gorunumlu': 'Metal Görünümlü',
};

export interface ModelMaterialMap {
  model: string;
  material: string;
}

/**
 * Authoritative mapping table for kitchen models and materials derived from
 * Adeko / Winner / MicroCAD XML model & group codes (T6, T7, V1, V2, F1, F2, S1, N1, N2, HA, B3, B8, etc.)
 */
export const KITCHEN_MODEL_MATERIAL_MAP: Record<string, ModelMaterialMap> = {
  // Toscana (T6, T7)
  'T6': { model: 'TOSCANA', material: 'Mat Lake' },
  'T7': { model: 'TOSCANA', material: 'Parlak Lake' },

  // Vega (V1, V2)
  'V1': { model: 'VEGA', material: 'Mat Lake' },
  'V2': { model: 'VEGA', material: 'Mat Polimerik' },

  // Fresca (F1, F2)
  'F1': { model: 'FRESCA', material: 'Mat Lake' },
  'F2': { model: 'FRESCA', material: 'Mat Polimerik' },

  // Siena (S1)
  'S1': { model: 'SIENA', material: 'Mat Pet' },

  // Norda (N1, N2)
  'N1': { model: 'NORDA', material: 'Doğal Ahşap Kaplama' },
  'N2': { model: 'NORDA', material: 'Mat Lake' },

  // Prime / Inova family (HA, B3, B8, T1, CP, CM, DM, N7, MG, M1, M2, X1, M3, PR1)
  'HA': { model: 'PRIME', material: 'Doğal Ahşap Kaplama' },
  'B3': { model: 'PRIME', material: 'Parlak Lake' },
  'B8': { model: 'PRIME', material: 'Mat Lake' },
  'T1': { model: 'PRIME', material: 'Mat Polimerik' },
  'CP': { model: 'PRIME', material: 'Parlak Kristal Pet' },
  'CM': { model: 'PRIME', material: 'Mat Kristal Pet' },
  'DM': { model: 'PRIME', material: 'Dokulu Melamin' },
  'N7': { model: 'PRIME', material: 'Akrilik Pet' },
  'MG': { model: 'PRIME', material: 'Metal Görünümlü' },
  'M1': { model: 'PRIME', material: 'Düz Melamin' },
  'M2': { model: 'PRIME', material: 'Soft Lak' },
  'X1': { model: 'PRIME', material: 'Dokulu Melamin' },
  'M3': { model: 'PRIME', material: 'Soft Pet' },
  'PR1': { model: 'PRIME', material: 'Proje Renkleri' },

  // TMM Kod aliases for completeness
  'ND': { model: 'PRIME', material: 'Doğal Ahşap Kaplama' },
  'GL': { model: 'PRIME', material: 'Parlak Lake' },
  'ML': { model: 'PRIME', material: 'Mat Lake' },
  'DP': { model: 'PRIME', material: 'Mat Polimerik' },
  'CPT': { model: 'PRIME', material: 'Parlak Kristal Pet' },
  'CMT': { model: 'PRIME', material: 'Mat Kristal Pet' },
  'FML': { model: 'FRESCA', material: 'Mat Lake' },
  'FDP': { model: 'FRESCA', material: 'Mat Polimerik' },
  'DMT': { model: 'PRIME', material: 'Dokulu Melamin' },
  'GA': { model: 'PRIME', material: 'Akrilik Pet' },
  'M1T': { model: 'PRIME', material: 'Düz Melamin' },
  'M2T': { model: 'PRIME', material: 'Soft Lak' },
  'X1T': { model: 'PRIME', material: 'Dokulu Melamin' },
  'M3T': { model: 'PRIME', material: 'Soft Pet' },
  'PR1T': { model: 'PRIME', material: 'Proje Renkleri' },
};

export interface GlassDoorInfo {
  profileColor: string;
  glassType: string;
}

/**
 * Maps "Kapak Kodu" (glazed/glass door style codes, e.g. NV3F, ZD5O) found in the
 * KITCHENDMODEL tag (or as the CMO_KAP_RENK1/2 prefix) to their Profil Rengi + Cam Tipi.
 * Source: xml_eslesme.xlsx. Note: the source sheet mistyped the Ondüle suffix as digit "0"
 * for ND3_, ND5_, ZD3_, ZD5_ (should be letter "O", confirmed against real project XML where
 * KITCHENDMODEL reads "ZD5O") — corrected here. "MatSiyah" (no space) also normalized to "Mat Siyah".
 */
export const KAPAK_KODU_GLASS_DOOR_MAP: Record<string, GlassDoorInfo> = {
  'NV3F': { profileColor: 'Mat Siyah', glassType: 'Füme Reflekte Cam' },
  'NV4F': { profileColor: 'Antrasit', glassType: 'Füme Reflekte Cam' },
  'NV5F': { profileColor: 'Satine Bronz', glassType: 'Füme Reflekte Cam' },

  'ND1D': { profileColor: 'Parlak Alüminyum', glassType: 'Düz Cam' },
  'ND1S': { profileColor: 'Parlak Alüminyum', glassType: 'Sable Cam' },
  'ND1O': { profileColor: 'Parlak Alüminyum', glassType: 'Ondüle Cam' },
  'ND3D': { profileColor: 'Mat Siyah', glassType: 'Düz Cam' },
  'ND3S': { profileColor: 'Mat Siyah', glassType: 'Sable Cam' },
  'ND3O': { profileColor: 'Mat Siyah', glassType: 'Ondüle Cam' },
  'ND3F': { profileColor: 'Mat Siyah', glassType: 'Füme Reflekte Cam' },
  'ND5D': { profileColor: 'Satine Bronz', glassType: 'Düz Cam' },
  'ND5S': { profileColor: 'Satine Bronz', glassType: 'Sable Cam' },
  'ND5O': { profileColor: 'Satine Bronz', glassType: 'Ondüle Cam' },
  'ND5F': { profileColor: 'Satine Bronz', glassType: 'Füme Reflekte Cam' },

  'ZD1D': { profileColor: 'Parlak Alüminyum', glassType: 'Düz Cam' },
  'ZD1S': { profileColor: 'Parlak Alüminyum', glassType: 'Sable Cam' },
  'ZD1O': { profileColor: 'Parlak Alüminyum', glassType: 'Ondüle Cam' },
  'ZD3D': { profileColor: 'Mat Siyah', glassType: 'Düz Cam' },
  'ZD3S': { profileColor: 'Mat Siyah', glassType: 'Sable Cam' },
  'ZD3O': { profileColor: 'Mat Siyah', glassType: 'Ondüle Cam' },
  'ZD3F': { profileColor: 'Mat Siyah', glassType: 'Füme Reflekte Cam' },
  'ZD5D': { profileColor: 'Satine Bronz', glassType: 'Düz Cam' },
  'ZD5S': { profileColor: 'Satine Bronz', glassType: 'Sable Cam' },
  'ZD5O': { profileColor: 'Satine Bronz', glassType: 'Ondüle Cam' },
  'ZD5F': { profileColor: 'Satine Bronz', glassType: 'Füme Reflekte Cam' },

  'NY3F': { profileColor: 'Mat Siyah', glassType: 'Füme Reflekte Cam' },
  'NY5F': { profileColor: 'Satine Bronz', glassType: 'Füme Reflekte Cam' },
  'ZY3F': { profileColor: 'Mat Siyah', glassType: 'Füme Reflekte Cam' },
  'ZY5F': { profileColor: 'Satine Bronz', glassType: 'Füme Reflekte Cam' },
};

/**
 * Resolves Profil Rengi + Cam Tipi from a raw KITCHENDMODEL code or door-color-prefix code
 * (e.g. "ZD5O" from KITCHENDMODEL, or the "ZD5O" prefix of CMO_KAP_RENK1="ZD5O_05_Satin Bronz").
 */
export function resolveGlassDoorInfo(rawCode: string): GlassDoorInfo | null {
  if (!rawCode) return null;
  const cleanCode = rawCode.split(/[_.\-\s]+/)[0].trim().toUpperCase();
  return KAPAK_KODU_GLASS_DOOR_MAP[cleanCode] || null;
}

/**
 * Resolves exact kitchen model and door material from KITCHENDMODEL code, door color prefix,
 * or module product code prefix (MY... -> INOVA, MN... -> PRIME).
 */
export function resolveModelAndMaterialFromCode(
  kitchenModelCode: string,
  doorColorRaw: string,
  modelGroupRaw: string,
  availableModels: string[],
  moduleCodePrefixModel: string = ''
): { model: string; material: string } {
  const cleanCode = (kitchenModelCode || '').split(/[_.\-\s]+/)[0].trim().toUpperCase();
  const colorPrefix = (doorColorRaw || '').split(/[_.\-\s]+/)[0].trim().toUpperCase();

  let mapped = KITCHEN_MODEL_MATERIAL_MAP[cleanCode] || KITCHEN_MODEL_MATERIAL_MAP[colorPrefix];

  if (!mapped && kitchenModelCode) {
    const matched = findBestMatch(kitchenModelCode, availableModels);
    if (matched) {
      mapped = { model: matched, material: '' };
    }
  }

  let rawModel = mapped ? mapped.model : modelGroupRaw;

  // For shared Prime/Inova family (e.g. M2, B8, B3, HA, T1, CP, CM, DM, N7, MG, M1, X1, M3, PR1):
  // Differentiate using module code prefix (MY... -> INOVA, MN... -> PRIME) first, then fallback to modelGroupRaw
  if (mapped && (mapped.model.toUpperCase() === 'PRIME' || mapped.model.toUpperCase() === 'INOVA')) {
    if (moduleCodePrefixModel) {
      rawModel = moduleCodePrefixModel;
    } else {
      const normGroup = normalizeTurkishText(modelGroupRaw);
      if (normGroup.includes('inova')) {
        rawModel = 'INOVA';
      }
    }
  }

  // Always resolve model through findBestMatch against availableModels so it matches exact case ("VEGA", "TOSCANA", "PRIME", "INOVA", etc.)
  const finalModel = findBestMatch(rawModel, availableModels) || rawModel || 'PRIME';
  const finalMaterial = mapped ? mapped.material : '';

  return {
    model: finalModel,
    material: finalMaterial
  };
}

/**
 * Calculates Levenshtein distance between two strings.
 */
export function getLevenshteinDistance(a: string, b: string): number {
  const tmp: number[][] = [];
  for (let i = 0; i <= a.length; i++) {
    tmp[i] = [i];
  }
  for (let j = 0; j <= b.length; j++) {
    tmp[0][j] = j;
  }
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      tmp[i][j] = Math.min(
        tmp[i - 1][j] + 1,
        tmp[i][j - 1] + 1,
        tmp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
  }
  return tmp[a.length][b.length];
}

/**
 * Normalizes Turkish characters and strips leading product code prefixes.
 * E.g., "V1_M023_Mat Lake Beton Yesili" -> "mat lake beton yesili"
 */
export function normalizeTurkishText(str: string): string {
  if (!str) return '';
  return str
    .replace(/^[A-Z0-9_]{2,10}_/, '') // strip prefixes like V1_M023_, CM_CM09_, KM_F236_
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/ş/g, 's')
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ö/g, 'o')
    .replace(/ı/g, 'i')
    .replace(/i̇/g, 'i')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Finds the best matching value from a list of candidate strings using Turkish normalization.
 */
export function findBestMatch(inputStr: string, candidates: string[]): string {
  if (!inputStr || !candidates || candidates.length === 0) return inputStr;
  const normInput = normalizeTurkishText(inputStr);

  // 1. Exact normalized match
  for (const cand of candidates) {
    if (normalizeTurkishText(cand) === normInput) {
      return cand;
    }
  }

  // 2. Partial / Contains match
  for (const cand of candidates) {
    const normCand = normalizeTurkishText(cand);
    if (normCand && (normCand.includes(normInput) || normInput.includes(normCand))) {
      return cand;
    }
  }

  // Fallback: Return cleaned version of input string
  return inputStr.replace(/^[A-Z0-9_]{2,10}_/, '').replace(/_/g, ' ').trim();
}

/**
 * Parses raw door color strings like "V1_M023_Mat Lake Beton Yesili" or "CM_CM03_Kristal Mat Acik Mocha"
 * into exact doorMaterial ("Mat Kristal Pet") and doorColor ("Açık Mocha") using system rules & alias mapping.
 */
export function parseDoorMaterialAndColor(doorColorRaw: string, modelName: string = 'PRIME') {
  if (!doorColorRaw) return { doorMaterial: '', doorColor: '' };

  const normRaw = normalizeTurkishText(doorColorRaw);
  const rules = KITCHEN_COMPATIBILITY_RULES_DEFAULT[modelName.toUpperCase()] || KITCHEN_COMPATIBILITY_RULES_DEFAULT['PRIME'];

  let matchedMaterial = '';
  let matchedColor = '';

  // 1. Check material aliases (e.g. "kristal mat" -> "Mat Kristal Pet")
  for (const [alias, matName] of Object.entries(MATERIAL_ALIASES)) {
    if (normRaw.includes(alias)) {
      matchedMaterial = matName;
      break;
    }
  }

  // 2. If material is matched, search colors within that specific material first
  if (matchedMaterial && rules[matchedMaterial]) {
    let maxLen = 0;
    for (const color of rules[matchedMaterial]) {
      const normColor = normalizeTurkishText(color);
      if (normColor && normRaw.includes(normColor)) {
        if (normColor.length > maxLen) {
          maxLen = normColor.length;
          matchedColor = color;
        }
      }
    }
  }

  // 3. Fallback search across all materials if not found
  if (!matchedColor && rules) {
    let maxLen = 0;
    for (const [mat, colors] of Object.entries(rules)) {
      for (const color of colors) {
        const normColor = normalizeTurkishText(color);
        if (normColor && normRaw.includes(normColor)) {
          if (normColor.length > maxLen) {
            maxLen = normColor.length;
            matchedColor = color;
            if (!matchedMaterial) matchedMaterial = mat;
          }
        }
      }
    }
  }

  if (!matchedColor) {
    matchedColor = doorColorRaw.replace(/^[A-Z0-9_]{2,10}_/, '').replace(/_/g, ' ').trim();
  }

  return { doorMaterial: matchedMaterial, doorColor: matchedColor };
}

/**
 * Finds a catalog item that matches an XML product code.
 * 1. Exact match (e.g. MKGCK01EB0064)
 * 2. Substring match: catalog code is contained within XML code (e.g. K2KDM inside MNAK2KDM)
 * 3. Fuzzy match: Levenshtein distance <= 1 for code lengths >= 8 (e.g. MDYLEDTRAFO vs MDYDLEDTRAFO)
 * Priority is given to longer matching catalog codes.
 */
export function findCatalogMatch(xmlCode: string, catalog: CatalogItem[]): CatalogItem | null {
  if (!xmlCode || !Array.isArray(catalog) || catalog.length === 0) return null;
  const upperXml = xmlCode.trim().toUpperCase();
  const cleanXml = upperXml.replace(/[^A-Z0-9]/g, '');

  // 1. Exact match by raw code or cleaned alphanumeric code
  for (const item of catalog) {
    if (item && item.code) {
      const upperCat = item.code.trim().toUpperCase();
      if (upperCat === upperXml || (cleanXml && upperCat.replace(/[^A-Z0-9]/g, '') === cleanXml)) {
        return item;
      }
    }
  }

  // 2. Substring & Prefix match (sorted by code length descending for priority)
  const sortedCatalog = catalog.filter(c => c && c.code && c.code.trim().length >= 3);
  sortedCatalog.sort((a, b) => b.code.trim().length - a.code.trim().length);

  for (const catItem of sortedCatalog) {
    const upperCatCode = catItem.code.trim().toUpperCase();
    const cleanCatCode = upperCatCode.replace(/[^A-Z0-9]/g, '');

    if (cleanCatCode.length >= 3) {
      // Substring match on raw or cleaned codes
      if (upperXml.includes(upperCatCode) || (cleanXml && cleanCatCode && cleanXml.includes(cleanCatCode))) {
        return catItem;
      }

      // Check after stripping CAD prefixes (MNA, MNU, MNB, MYA, MZK, DWG, CMO_, etc.)
      const strippedXml = upperXml.replace(/^(MNA|MNU|MNB|MYA|MZK|DWG|CMO_|CAD_|INT_)/, '');
      const strippedCleanXml = strippedXml.replace(/[^A-Z0-9]/g, '');
      if (strippedCleanXml && cleanCatCode && (strippedCleanXml.startsWith(cleanCatCode) || strippedCleanXml.endsWith(cleanCatCode))) {
        return catItem;
      }
    }
  }

  // 3. Fuzzy match fallback: close match with Levenshtein distance <= 1 for code length >= 8
  for (const catItem of sortedCatalog) {
    const upperCatCode = catItem.code.trim().toUpperCase();
    const cleanCatCode = upperCatCode.replace(/[^A-Z0-9]/g, '');
    if (cleanCatCode.length >= 8 && cleanXml.length >= 8) {
      if (getLevenshteinDistance(cleanXml, cleanCatCode) <= 1) {
        return catItem;
      }
    }
  }

  return null;
}

/**
 * Finds a catalog item corresponding to a Door Color (category === 'Renk').
 */
export function findCatalogColorItem(doorColorName: string, catalog: CatalogItem[]): CatalogItem | null {
  if (!doorColorName || !Array.isArray(catalog) || catalog.length === 0) return null;
  const normDoor = normalizeTurkishText(doorColorName);
  const colorItems = catalog.filter(c => c && (c.category === 'Renk' || c.category === 'Kapak Rengi'));

  // 1. Exact match by code or name
  for (const c of colorItems) {
    if (normalizeTurkishText(c.code) === normDoor || normalizeTurkishText(c.name) === normDoor) {
      return c;
    }
  }

  // 2. Partial match
  for (const c of colorItems) {
    const normCode = normalizeTurkishText(c.code);
    const normName = normalizeTurkishText(c.name);
    if ((normCode && normDoor.includes(normCode)) || (normName && normDoor.includes(normName)) || (normCode && normCode.includes(normDoor))) {
      return c;
    }
  }

  return null;
}

export const BODY_COLOR_IMAGES: Record<string, string> = {
  'Koton Vizon': 'https://res.cloudinary.com/drp4askpq/image/upload/v1785573698/Ekran_Resmi_2026-08-01_11.41.10_eirunj.png',
  'Antrasit': 'https://res.cloudinary.com/drp4askpq/image/upload/v1785490685/Go%CC%88vde_Antrasit_krgzh2.png',
  'Beyaz': 'https://res.cloudinary.com/drp4askpq/image/upload/v1785490683/Go%CC%88vde_Beyaz_c7g4wv.png',
};

/**
 * Finds a catalog item corresponding to a Body Color (category === 'Gövde Rengi' or 'Gövde').
 */
export function findCatalogBodyColorItem(bodyColorName: string, catalog: CatalogItem[]): CatalogItem | null {
  if (!bodyColorName) return null;
  const normBody = normalizeTurkishText(bodyColorName);

  if (Array.isArray(catalog) && catalog.length > 0) {
    const bodyItems = catalog.filter(c => c && (c.category === 'Gövde Rengi' || c.category === 'Gövde'));

    // 1. Exact match by code or name among Gövde Rengi items
    for (const c of bodyItems) {
      if (normalizeTurkishText(c.code) === normBody || normalizeTurkishText(c.name) === normBody) {
        return c;
      }
    }

    // 2. Partial match among Gövde Rengi items
    for (const c of bodyItems) {
      const normCode = normalizeTurkishText(c.code);
      const normName = normalizeTurkishText(c.name);
      if ((normCode && normBody.includes(normCode)) || (normName && normBody.includes(normName))) {
        return c;
      }
    }
  }

  // 3. Fallback to Cloudinary mapping
  const matchedKey = Object.keys(BODY_COLOR_IMAGES).find(k => normalizeTurkishText(k) === normBody);
  if (matchedKey) {
    return {
      category: 'Gövde Rengi',
      code: matchedKey,
      name: `${matchedKey} Gövde Rengi`,
      image: BODY_COLOR_IMAGES[matchedKey]
    };
  }

  return null;
}

/**
 * Parses CAD/3D Kitchen XML project file (Adeko / Winner / MicroCAD XML format)
 * and extracts project metadata, configuration settings, and module spec items.
 * STRICT UNIQUE MODE: Only items that match a product code in the catalog are added,
 * and EACH matched product code is added EXACTLY ONCE (1 item).
 */
export function parseXmlProject(
  xmlText: string,
  fileName: string = '',
  catalog: CatalogItem[] = [],
  availableModels: string[] = [],
  availableBodyColors: string[] = [],
  includeUnmatchedAccessories: boolean = false
): ParsedXmlProject {
  // 1. Extract Customer Name from filename or project tags
  let clientName = '';
  const customerTagMatch = xmlText.match(/<(?:CUSTOMER_NAME|CUSTOMER|CLIENT_NAME|PROJECT_NAME)>(.*?)<\/(?:CUSTOMER_NAME|CUSTOMER|CLIENT_NAME|PROJECT_NAME)>/i);
  
  if (customerTagMatch && customerTagMatch[1] && customerTagMatch[1].trim()) {
    clientName = customerTagMatch[1].trim();
  } else if (fileName) {
    const baseName = fileName.split('.')[0]; // e.g. "AYŞEGÜL ÜRKMEZ (kristal kaplama)"
    const cleanName = baseName.replace(/\(.*?\)/g, '').split('-')[0].trim();
    if (cleanName) {
      clientName = cleanName;
    }
  }

  // 2. Helper to extract single tag content (case-insensitive & attribute safe)
  const getTagContent = (tag: string): string => {
    const rx = new RegExp(`<${tag}(?:\\s+[^>]*)?>(.*?)</${tag}>`, 'is');
    const match = xmlText.match(rx);
    return match ? match[1].trim() : '';
  };

  const kitchenModelRaw = getTagContent('KITCHENDMODEL') ||
                          getTagContent('KITCHENMODEL') ||
                          getTagContent('KITCHENDMODEL1') ||
                          getTagContent('KITCHEN_MODEL') ||
                          getTagContent('MODEL');
  const kitchenModelRaw2 = getTagContent('KITCHENDMODEL2') || getTagContent('KITCHENMODEL2');
  const modelGroupRaw1 = getTagContent('MODELGROUP') || getTagContent('MODEL_GROUP');
  const modelGroupRaw2 = getTagContent('MODELGROUP2') || getTagContent('MODEL_GROUP2');
  const bodyMatRaw = getTagContent('CMO_MALZEME') || getTagContent('KITCHENBODYMAT');
  const doorColorRaw1 = getTagContent('CMO_KAP_RENK1');
  const doorColorRaw2 = getTagContent('CMO_KAP_RENK2');
  const handleCodeRaw = getTagContent('CMO_KULP');

  // Resolve Profil Rengi + Cam Tipi for glazed door style codes (e.g. KITCHENDMODEL="ZD5O",
  // or CMO_KAP_RENK1="ZD5O_05_Satin Bronz") against the Kapak Kodu lookup table.
  // A project can mix solid-door units (KITCHENDMODEL="V1") with glazed units (KITCHENDMODEL="ZD5O")
  // in the same file, so every occurrence of these tags is scanned, not just the first.
  const glassDoorCandidateTags = ['KITCHENDMODEL', 'KITCHENMODEL', 'KITCHENDMODEL1', 'KITCHENDMODEL2', 'KITCHENMODEL2', 'KITCHEN_MODEL', 'MODEL', 'CMO_KAP_RENK1', 'CMO_KAP_RENK2'];
  let glassDoorInfo: GlassDoorInfo | null = null;
  for (const tag of glassDoorCandidateTags) {
    const rx = new RegExp(`<${tag}>(.*?)<\\/${tag}>`, 'gis');
    let m: RegExpExecArray | null;
    while ((m = rx.exec(xmlText)) !== null) {
      const info = resolveGlassDoorInfo(m[1]);
      if (info) {
        glassDoorInfo = info;
        break;
      }
    }
    if (glassDoorInfo) break;
  }

  // Detect model from product/module code prefixes in the XML (MY/MZ... -> INOVA, MN... -> PRIME)
  let detectedModelFromModuleCodes = '';
  const allCodes = Array.from(xmlText.matchAll(/<(?:CODE|UNIQUECODE)>(.*?)<\/(?:CODE|UNIQUECODE)>/gi)).map(m => m[1].trim().toUpperCase());

  let myMzCount = 0;
  let mnCount = 0;

  for (const code of allCodes) {
    if (code.startsWith('MY') || code.startsWith('MZ')) {
      myMzCount++;
    } else if (code.startsWith('MN')) {
      mnCount++;
    }
  }

  if (mnCount > myMzCount && mnCount > 0) {
    detectedModelFromModuleCodes = 'PRIME';
  } else if (myMzCount > mnCount && myMzCount > 0) {
    detectedModelFromModuleCodes = 'INOVA';
  }

  // Resolve primary model and material (KITCHENDMODEL code e.g. T6 -> Toscana / Mat Lake, or MY... -> Inova vs MN... -> Prime)
  const primaryRes = resolveModelAndMaterialFromCode(
    kitchenModelRaw,
    doorColorRaw1,
    modelGroupRaw1 || modelGroupRaw2,
    availableModels,
    detectedModelFromModuleCodes
  );

  const modelGroup = primaryRes.model;
  const bodyMat = findBestMatch(bodyMatRaw, availableBodyColors);

  // Parse door material and color from door color raw string
  const { doorMaterial: parsedMat, doorColor } = parseDoorMaterialAndColor(doorColorRaw1 || doorColorRaw2, modelGroup);
  const doorMaterial = primaryRes.material || parsedMat;

  // Resolve secondary door material and color ONLY if explicitly defined with a distinct model code or second door color
  let extraMaterials: string[] = [];
  let extraDoorColors: string[] = [];
  let extraModels: string[] = [];

  // Ignore boilerplate MODELGROUP2 ("Prime") if KITCHENDMODEL2 is empty and modelGroupRaw2 is identical to modelGroupRaw1
  const hasExplicitSecondaryModel = Boolean(
    kitchenModelRaw2 || 
    (modelGroupRaw2 && modelGroupRaw1 && modelGroupRaw2.trim().toUpperCase() !== modelGroupRaw1.trim().toUpperCase())
  );

  let secondaryRes: { model: string; material: string } | null = null;
  if (hasExplicitSecondaryModel) {
    secondaryRes = resolveModelAndMaterialFromCode(
      kitchenModelRaw2,
      doorColorRaw2,
      modelGroupRaw2,
      availableModels,
      detectedModelFromModuleCodes
    );

    if (secondaryRes.model && secondaryRes.model !== modelGroup && !extraModels.includes(secondaryRes.model)) {
      extraModels.push(secondaryRes.model);
    }
  }

  if (doorColorRaw2 && doorColorRaw2 !== doorColorRaw1) {
    const secondaryResult = parseDoorMaterialAndColor(doorColorRaw2, secondaryRes?.model || modelGroup);
    const secMaterial = secondaryRes?.material || secondaryResult.doorMaterial;
    if (secMaterial && secMaterial !== doorMaterial && !extraMaterials.includes(secMaterial)) {
      extraMaterials.push(secMaterial);
    }
    if (secondaryResult.doorColor && secondaryResult.doorColor !== doorColor && !extraDoorColors.includes(secondaryResult.doorColor)) {
      extraDoorColors.push(secondaryResult.doorColor);
    }
  }

  // Filter out 'Renk', 'Kapak Rengi' or 'Gövde Rengi' items from catalog for the module block scanning,
  // since blocks represent physical product modules, not raw paint/door/body finishes.
  const productCatalog = catalog.filter(c => c && c.category !== 'Renk' && c.category !== 'Kapak Rengi' && c.category !== 'Gövde Rengi' && c.category !== 'Gövde');

  // 3. Extract Units & Accessories across all block tags
  // STRICT UNIQUE MODE: Add each product code EXACTLY ONCE (1 item)
  const items: SpecItem[] = [];
  const addedCodes = new Set<string>();
  const unmatchedAccessoriesMap = new Map<string, UnmatchedAccessory>();
  const blockTypes = ['UNITS', 'HANDLES', 'WORKTOPS', 'APPLIANCES', 'LEDLIGHTS', 'EXTRAS', 'DWGACCESSORIES', 'DOORS', 'PLINTHS', 'PLINTHSPARTS'];
  let idCounter = 1;
  let matchedCount = 0;
  let skippedCount = 0;

  blockTypes.forEach(tag => {
    const rx = new RegExp(`<${tag}>(.*?)<\\/${tag}>`, 'gs');
    let match: RegExpExecArray | null;

    while ((match = rx.exec(xmlText)) !== null) {
      const block = match[1];
      const codeMatch = block.match(/<CODE>(.*?)<\/CODE>/);

      if (codeMatch && codeMatch[1]) {
        const rawCode = codeMatch[1].split('$')[0].trim();
        if (!rawCode) continue;

        // Hariç tutulan ürün kodları eşleşmeye hiç girmez (ne ürün ne eşleşmeyen aksesuar)
        if (isExcludedXmlCode(rawCode)) continue;

        const nameMatch = block.match(/<NAME>(.*?)<\/NAME>/);
        const rawName = nameMatch && nameMatch[1] ? nameMatch[1].trim() : rawCode;

        // MATCH CHECK: Exact, Substring, or Fuzzy match
        const catalogMatch = findCatalogMatch(rawCode, productCatalog);

        // Bulanık/alt-dize eşleşme hariç tutulan bir katalog ürününe düştüyse de atla
        if (catalogMatch && isExcludedXmlCode(catalogMatch.code)) continue;

        if (!catalogMatch) {
          skippedCount++;
          const upperRawCode = rawCode.trim().toUpperCase();

          // Check if block is accessory/extra block (<DWGACCESSORIES> or <EXTRAS>)
          const isAccessoryBlock = tag === 'DWGACCESSORIES' || tag === 'EXTRAS' || block.includes('<GROUP>DWGACCESSORIES</GROUP>') || block.includes('<GROUP>EXTRAS</GROUP>');

          if (isAccessoryBlock) {
            if (!unmatchedAccessoriesMap.has(upperRawCode)) {
              unmatchedAccessoriesMap.set(upperRawCode, { code: rawCode, name: rawName || rawCode });
            }

            if (includeUnmatchedAccessories) {
              // UNIQUE CHECK: If this accessory code has already been added, skip duplicate addition!
              if (!addedCodes.has(upperRawCode)) {
                addedCodes.add(upperRawCode);
                matchedCount++;
                items.push({
                  id: `xml_acc_${Date.now()}_${idCounter++}`,
                  category: 'Aksesuar',
                  code: rawCode,
                  name: rawName || rawCode,
                  image: null
                });
              }
            }
          }
          continue; // Skip non-matching item from standard matching flow
        }

        const upperCode = catalogMatch.code.trim().toUpperCase();

        // UNIQUE CHECK: If this product code has already been added, skip duplicate addition!
        if (addedCodes.has(upperCode)) {
          continue;
        }

        addedCodes.add(upperCode);
        matchedCount++;

        items.push({
          id: `xml_${Date.now()}_${idCounter++}`,
          category: catalogMatch.category || 'Aksesuar',
          code: catalogMatch.code || rawCode,
          name: catalogMatch.name || rawCode,
          image: catalogMatch.image || null
        });
      }
    }
  });

  // Kapak Rengi / Gövde Rengi kalemleri XML'den otomatik eklenmez (kullanıcı katalogdan kendisi ekler).

  return {
    clientName,
    modelGroup,
    extraModels,
    doorMaterial,
    extraMaterials,
    doorColor,
    extraDoorColors,
    bodyMat,
    handleCode: handleCodeRaw,
    glassType: glassDoorInfo?.glassType,
    profileColor: glassDoorInfo?.profileColor,
    items,
    matchedCount,
    skippedCount,
    unmatchedAccessories: Array.from(unmatchedAccessoriesMap.values())
  };
}
